import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

/* Shared Excel / PDF / Print helpers (pattern matches ERP_WEB_APP P&L Comparison).
   columns: [{ key, label, num?, format? }]
     - num: right-align in PDF/Print
     - format: (value, row) => display value; defaults to row[key]
   opts:
     - kpis: [{ label, value }]  → rendered as a KPI strip above the table
     - subtitle: string          → shown under the title (PDF/Print)
     - sheetName: string         → Excel sheet name */

const cell = (row, col) => {
  const v = col.format ? col.format(row[col.key], row) : row[col.key];
  return v == null ? '' : v;
};

// Excel (.xlsx) via SheetJS — KPI label/value rows on top, then the data table.
export function exportExcel(filename, columns, rows, opts = {}) {
  const { kpis, sheetName = 'Sheet1' } = opts;
  const aoa = [];
  if (kpis && kpis.length) {
    aoa.push(kpis.map((k) => k.label));
    aoa.push(kpis.map((k) => k.value));
    aoa.push([]); // spacer row
  }
  aoa.push(columns.map((c) => c.label));
  rows.forEach((r) => aoa.push(columns.map((c) => cell(r, c))));
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = columns.map((c) => ({ wch: Math.max(String(c.label).length + 2, 12) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, String(sheetName).slice(0, 31));
  XLSX.writeFile(wb, `${filename}.xlsx`);
}

// PDF via jsPDF + autotable (brand-blue header); optional KPI strip on top.
export function exportPdf(filename, title, columns, rows, opts = {}) {
  const { subtitle, kpis } = opts;
  const doc = new jsPDF({ orientation: columns.length > 6 ? 'landscape' : 'portrait' });
  doc.setFontSize(13);
  doc.text(String(title), 14, 14);
  let startY = 20;
  if (subtitle) {
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(String(subtitle), 14, 20);
    doc.setTextColor(0);
    startY = 27;
  }
  if (kpis && kpis.length) {
    autoTable(doc, {
      startY,
      head: [kpis.map((k) => k.label)],
      body: [kpis.map((k) => String(k.value))],
      styles: { fontSize: 8, cellPadding: 3, halign: 'center' },
      headStyles: { fillColor: [227, 242, 253], textColor: [15, 76, 129], fontStyle: 'bold', halign: 'center' },
      margin: { left: 14, right: 14 },
    });
    startY = doc.lastAutoTable.finalY + 6;
  }
  autoTable(doc, {
    startY,
    head: [columns.map((c) => c.label)],
    body: rows.map((r) => columns.map((c) => String(cell(r, c)))),
    styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak' },
    headStyles: { fillColor: [15, 76, 129], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 248, 252] },
    columnStyles: columns.reduce((acc, col, i) => {
      if (col.num) acc[i] = { halign: 'right' };
      return acc;
    }, {}),
    margin: { left: 14, right: 14 },
  });
  doc.save(`${filename}.pdf`);
}

// Print — opens a print-formatted window; browser preview lets the user adjust.
export function printReport(title, columns, rows, opts = {}) {
  const { subtitle, kpis } = opts;
  const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const th = columns.map((c) => `<th>${esc(c.label)}</th>`).join('');
  const body = rows
    .map((r) => `<tr>${columns.map((c) => `<td style="${c.num ? 'text-align:right;' : ''}">${esc(cell(r, c))}</td>`).join('')}</tr>`)
    .join('');
  const kpiHtml = (kpis && kpis.length)
    ? `<table class="kpi"><tr>${kpis.map((k) => `<td><span class="kl">${esc(k.label)}</span><br><span class="kv">${esc(k.value)}</span></td>`).join('')}</tr></table>`
    : '';
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title>
    <style>
      @page { margin: 12mm; }
      * { box-sizing: border-box; }
      body { font-family: Arial, Helvetica, sans-serif; color: #1a1a2e; margin: 16px; }
      h1 { font-size: 18px; color: #0f4c81; margin: 0 0 2px; }
      .sub { font-size: 11px; color: #444466; margin-bottom: 10px; }
      table.kpi { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
      table.kpi td { border: 1px solid #c9d8ea; background: #f4f8ff; padding: 6px 10px; text-align: center; }
      table.kpi .kl { font-size: 9px; color: #567; text-transform: uppercase; letter-spacing: .3px; }
      table.kpi .kv { font-size: 14px; font-weight: 700; color: #0f4c81; }
      table.rpt { width: 100%; border-collapse: collapse; }
      table.rpt th, table.rpt td { border: 1px solid #d8d8d8; padding: 4px 7px; font-size: 11px; white-space: nowrap; }
      table.rpt th { background: #0f4c81; color: #fff; text-align: center; }
      table.rpt tbody tr:nth-child(even) td { background: #f5f8fc; }
      thead { display: table-header-group; }
      tr { page-break-inside: avoid; }
      @media print { body { margin: 0; } }
    </style></head><body>
    <h1>${esc(title)}</h1>
    <div class="sub">${subtitle ? esc(subtitle) + ' &nbsp;|&nbsp; ' : ''}Printed: ${new Date().toLocaleString('en-GB')} &nbsp;|&nbsp; ${rows.length} row(s)</div>
    ${kpiHtml}
    <table class="rpt"><thead><tr>${th}</tr></thead><tbody>${body}</tbody></table>
    </body></html>`;
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(html);
  w.document.close();
  setTimeout(() => w.print(), 300);
}
